import { useEffect, useRef, useState } from "react";
import { MealCommandCancelled } from "../../application/duplicate-food";
import { MealTransferError } from "../../application/meal-transfer";
import { correctVoiceMealRemotely, type ParsedVoiceMealDraft } from "../../application/remote-meal-parser";
import { extractVoiceFinishCommand, isVoiceCancelCommand } from "../../application/voice-commands";
import { mergeVoiceTranscript } from "../../application/voice-transcript";
import type { VoiceTranscriptionError, VoiceTranscriptionSession, VoiceTranscriptionProvider } from "../../application/voice-transcription";
import { useProcessingProgress } from "./useProcessingProgress";

export type MealCorrectionState = "idle" | "starting" | "listening" | "processing" | "error";
const correctionSteps = ["Разбираю команду…", "Сверяю продукты…", "Пересчитываю калории и БЖУ…", "Сохраняю изменения…", "Ответ занимает чуть больше времени…"] as const;

const errorText: Record<VoiceTranscriptionError, string> = {
  unsupported: "На этом устройстве браузерное распознавание речи недоступно.",
  permission: "Разрешите YumNote доступ к микрофону в настройках браузера.",
  "no-speech": "Речь не распознана. Попробуйте говорить ближе к телефону.",
  network: "Служба распознавания браузера сейчас недоступна.",
  aborted: "Запись отменена.",
  unknown: "Не удалось распознать речь. Попробуйте ещё раз.",
};

export function useMealCorrection(voiceProvider: VoiceTranscriptionProvider, onUpdated: (draft: ParsedVoiceMealDraft) => void, onCommand?: (answer: string, draft: ParsedVoiceMealDraft, itemIndex: number | null) => Promise<boolean>) {
  const [state, setState] = useState<MealCorrectionState>("idle");
  const [text, setText] = useState("");
  const [error, setError] = useState("");
  const progressText = useProcessingProgress(state === "processing", correctionSteps);
  const sessionRef = useRef<VoiceTranscriptionSession | null>(null);
  const draftRef = useRef<ParsedVoiceMealDraft | null>(null);
  const itemIndexRef = useRef<number | null>(null);
  const finalTextRef = useRef("");
  const onUpdatedRef = useRef(onUpdated);
  onUpdatedRef.current = onUpdated;
  const onCommandRef = useRef(onCommand);
  onCommandRef.current = onCommand;

  const reset = () => {
    sessionRef.current?.abort();
    sessionRef.current = null;
    draftRef.current = null;
    itemIndexRef.current = null;
    finalTextRef.current = "";
    setText("");
    setError("");
    setState("idle");
  };

  const submit = async (answer: string) => {
    if (isVoiceCancelCommand(answer)) { reset(); return; }
    const draft = draftRef.current;
    const itemIndex = itemIndexRef.current;
    if (!draft || itemIndex === null || !answer.trim()) {
      reset();
      return;
    }
    setState("processing");
    try {
      if (await onCommandRef.current?.(answer.trim(), draft, itemIndex < 0 ? null : itemIndex)) { reset(); return; }
      const updated = await correctVoiceMealRemotely(draft, itemIndex < 0 ? null : itemIndex, answer.trim());
      onUpdatedRef.current(updated);
      reset();
    } catch (problem) {
      if (problem instanceof MealCommandCancelled) { reset(); return; }
      sessionRef.current = null;
      const remote = problem as Error & { code?: string; errorId?: string };
      console.error("meal_correction_failed", { code: remote.code, errorId: remote.errorId });
      setError(problem instanceof MealTransferError ? problem.message : remote.code === "model_timeout"
        ? "Я не успел обработать команду. Нажмите микрофон и повторите — текущая еда не изменилась."
        : "Не удалось применить изменение. Текущая еда не изменилась — попробуйте ещё раз.");
      setState("error");
    }
  };

  const start = (draft: ParsedVoiceMealDraft, itemIndex = -1) => {
    sessionRef.current?.abort();
    draftRef.current = draft;
    itemIndexRef.current = itemIndex;
    finalTextRef.current = "";
    setText("");
    setError("");
    setState("starting");
    sessionRef.current = voiceProvider.start("ru-RU", {
      onListening: () => setState("listening"),
      onCaptureEnd: () => setState("processing"),
      onInterim: incoming => {
        const finish = extractVoiceFinishCommand(mergeVoiceTranscript(finalTextRef.current, incoming));
        setText(finish.text);
        if (finish.finished && finish.text) {
          finalTextRef.current = finish.text;
          setState("processing");
          sessionRef.current?.stop();
        }
      },
      onFinal: incoming => {
        const finish = extractVoiceFinishCommand(mergeVoiceTranscript(finalTextRef.current, incoming));
        finalTextRef.current = finish.text;
        setText(finish.text);
        if (finish.finished && finish.text) {
          setState("processing");
          sessionRef.current?.stop();
        }
      },
      onError: problem => {
        if (problem === "aborted") return;
        sessionRef.current = null;
        setError(errorText[problem]);
        setState("error");
      },
      onEnd: () => {
        sessionRef.current = null;
        const answer = finalTextRef.current.trim();
        if (answer) void submit(answer);
        else reset();
      },
    });
  };

  const stop = () => {
    const answer = (finalTextRef.current || text).trim();
    if (!answer) {
      reset();
      return;
    }
    finalTextRef.current = answer;
    setState("processing");
    sessionRef.current?.stop();
  };

  const toggle = (draft: ParsedVoiceMealDraft) => {
    if (state === "listening") stop();
    else if (state !== "processing") start(draft);
  };
  const submitText = (draft: ParsedVoiceMealDraft, answer: string) => {
    const command = answer.trim();
    if (!command || state === "processing") return;
    sessionRef.current?.abort();
    sessionRef.current = null;
    draftRef.current = draft;
    itemIndexRef.current = -1;
    finalTextRef.current = command;
    setText(command);
    setError("");
    void submit(command);
  };

  useEffect(() => reset, []);

  return { state, text, error, progressText, active: state !== "idle", start, stop, toggle, submitText, cancel: reset };
}
